/**
 * Desktop - Handles desktop-specific UI functionality
 * 
 * Required from parent (voiceInterface):
 * - shadowRoot: ShadowRoot | Document
 * - isMobile: boolean
 * - isActive: boolean
 * - isMicMuted: boolean
 * - t(key): string (translation helper)
 * 
 * State owned by this module:
 * - None (stateless UI updates)
 */

export class Desktop {
  constructor(voiceInterface) {
    this.voiceInterface = voiceInterface;
    this.config = voiceInterface.config;
    this.shadowRoot = voiceInterface.shadowRoot;
    this._liveWaveformInterval = null;
  }

  /**
   * Helper to access translation method from parent
   */
  t(key) {
    return this.voiceInterface.t(key);
  }

  /**
   * Adjust UI sizes based on container height
   */
  adjustSizesForContainer() {
    const voiceInterface = this.shadowRoot.getElementById('voiceInterface');
    if (!voiceInterface) return;
    
    const containerHeight = voiceInterface.clientHeight || voiceInterface.offsetHeight || 400;
    const containerWidth = voiceInterface.clientWidth || voiceInterface.offsetWidth || 400;
    
    // Calculate sizes based on container height
    let avatarSize, statusHeight, transcriptHeight, buttonSize, primaryButtonSize, gapSize, titleSize;
    
    if (containerHeight <= 350) {
      avatarSize = 80;
      statusHeight = 40;
      transcriptHeight = 80;
      buttonSize = 48;
      primaryButtonSize = 54;
      gapSize = 6;
      titleSize = 12;
    } else if (containerHeight <= 450) {
      avatarSize = 100;
      statusHeight = 50;
      transcriptHeight = 100;
      buttonSize = 56;
      primaryButtonSize = 62;
      gapSize = 8;
      titleSize = 14;
    } else if (containerHeight <= 550) {
      avatarSize = 120;
      statusHeight = 60;
      transcriptHeight = 120;
      buttonSize = 64;
      primaryButtonSize = 70;
      gapSize = 10;
      titleSize = 16;
    } else if (containerHeight <= 700) {
      avatarSize = 140;
      statusHeight = 70;
      transcriptHeight = 140;
      buttonSize = 72;
      primaryButtonSize = 78;
      gapSize = 12;
      titleSize = 18;
    } else {
      avatarSize = 160;
      statusHeight = 80;
      transcriptHeight = 160;
      buttonSize = 80;
      primaryButtonSize = 86;
      gapSize = 14;
      titleSize = 20;
    }
    
    // Apply sizes to avatar
    const avatar = this.shadowRoot.querySelector('.voice-avatar, .voice-avatar-active');
    if (avatar) {
      avatar.style.width = `${avatarSize}px`;
      avatar.style.height = `${avatarSize}px`;
      avatar.style.fontSize = `${avatarSize * 0.5}px`;
    }
    
    // Apply sizes to status
    const statusTitle = this.shadowRoot.querySelector('.voice-status-title');
    const statusSubtitle = this.shadowRoot.querySelector('.voice-status-subtitle');
    if (statusTitle) statusTitle.style.fontSize = `${titleSize}px`;
    if (statusSubtitle) statusSubtitle.style.fontSize = `${Math.max(10, titleSize - 4)}px`;
    
    // Apply sizes to transcript
    const transcript = this.shadowRoot.querySelector('.voice-transcript');
    if (transcript) {
      transcript.style.minHeight = `${transcriptHeight}px`;
      transcript.style.maxHeight = `${transcriptHeight}px`;
      transcript.style.height = `${transcriptHeight}px`;
      transcript.style.width = `min(360px, ${containerWidth - 40}px)`;
      transcript.style.boxSizing = 'border-box';
    }
    
    const transcriptLabel = this.shadowRoot.querySelector('.transcript-label');
    if (transcriptLabel) {
      transcriptLabel.style.fontSize = `${Math.max(8, titleSize - 4)}px`;
    }
    
    const transcriptText = this.shadowRoot.querySelector('.transcript-text');
    if (transcriptText) {
      transcriptText.style.fontSize = `${Math.max(10, titleSize - 2)}px`;
    }
    
    // Apply sizes to text input area
    const textInputArea = this.shadowRoot.querySelector('.voice-text-input-area');
    if (textInputArea) {
      const inputWrapper = textInputArea.querySelector('.voice-input-wrapper');
      const inputField = textInputArea.querySelector('.voice-text-input');
      const sendBtn = textInputArea.querySelector('.voice-send-btn');
      const hintText = textInputArea.querySelector('.voice-input-hint');
      
      if (inputWrapper && sendBtn) {
        const sendBtnSize = Math.max(32, Math.min(40, buttonSize * 0.7));
        sendBtn.style.width = `${sendBtnSize}px`;
        sendBtn.style.height = `${sendBtnSize}px`;
        
        const sendBtnIcon = sendBtn.querySelector('svg');
        if (sendBtnIcon) {
          const iconSize = Math.max(14, Math.min(18, sendBtnSize * 0.45));
          sendBtnIcon.style.width = `${iconSize}px`;
          sendBtnIcon.style.height = `${iconSize}px`;
        }
      }
      
      if (inputField) {
        inputField.style.fontSize = `${Math.max(11, Math.min(14, titleSize))}px`;
        inputField.style.padding = `${Math.max(6, Math.min(10, Math.floor(buttonSize * 0.15)))}px 0`;
      }
      
      if (hintText) {
        hintText.style.fontSize = `${Math.max(8, Math.min(11, titleSize - 4))}px`;
        hintText.style.marginTop = `${Math.max(3, Math.min(6, Math.floor(gapSize * 0.6)))}px`;
      }
    }
    
    // Apply sizes to control buttons
    const secondaryButtons = this.shadowRoot.querySelectorAll('.voice-control-btn.secondary');
    secondaryButtons.forEach(btn => {
      btn.style.width = `${buttonSize}px`;
      btn.style.height = `${buttonSize}px`;
      
      const svg = btn.querySelector('svg');
      if (svg) {
        const iconSize = buttonSize * 0.65;
        svg.style.setProperty('width', `${iconSize}px`, 'important');
        svg.style.setProperty('height', `${iconSize}px`, 'important');
        svg.style.setProperty('min-width', `${iconSize}px`, 'important');
        svg.style.setProperty('min-height', `${iconSize}px`, 'important');
      }
    });
    
    const primaryButton = this.shadowRoot.querySelector('.voice-control-btn.primary');
    if (primaryButton) {
      primaryButton.style.width = `${primaryButtonSize}px`;
      primaryButton.style.height = `${primaryButtonSize}px`;
      
      const svg = primaryButton.querySelector('svg');
      if (svg) {
        const iconSize = primaryButtonSize * 0.70;
        svg.style.setProperty('width', `${iconSize}px`, 'important');
        svg.style.setProperty('height', `${iconSize}px`, 'important');
        svg.style.setProperty('min-width', `${iconSize}px`, 'important');
        svg.style.setProperty('min-height', `${iconSize}px`, 'important');
      }
    }
    
    // Apply to start call button
    const startCallBtn = this.shadowRoot.querySelector('.start-call-btn');
    if (startCallBtn) {
      let btnHeight, btnFontSize;
      if (containerHeight <= 350) {
        btnHeight = 48;
        btnFontSize = 14;
      } else if (containerHeight <= 450) {
        btnHeight = 52;
        btnFontSize = 15;
      } else if (containerHeight <= 550) {
        btnHeight = 58;
        btnFontSize = 17;
      } else if (containerHeight <= 700) {
        btnHeight = 62;
        btnFontSize = 18;
      } else {
        btnHeight = 64;
        btnFontSize = 18;
      }
      startCallBtn.style.height = `${btnHeight}px`;
      startCallBtn.style.fontSize = `${btnFontSize}px`;
      
      const btnIcon = startCallBtn.querySelector('svg');
      if (btnIcon) {
        const iconSize = Math.max(22, btnHeight * 0.5);
        btnIcon.style.width = `${iconSize}px`;
        btnIcon.style.height = `${iconSize}px`;
      }
    }
    
  }

  /**
   * Start desktop waveform animation
   */
  startDesktopWaveformAnimation() {
    if (this.voiceInterface.isMobile) return;
    
    const waveform = this.shadowRoot.getElementById('desktopWaveform');
    if (!waveform) return;
    
    // Remove paused class to start animation
    waveform.classList.remove('paused');
    
    // Update status based on listening state
    this.updateDesktopStatus();
  }
  
  /**
   * Stop desktop waveform animation
   */
  stopDesktopWaveformAnimation() {
    if (this.voiceInterface.isMobile) return;
    
    const waveform = this.shadowRoot.getElementById('desktopWaveform');
    if (waveform) {
      waveform.classList.add('paused');
    }
  }
  
  /**
   * Update desktop status display
   */
  updateDesktopStatus() {
    if (this.voiceInterface.isMobile) return;
    
    // Update both old and new status text elements
    const statusText = this.shadowRoot.getElementById('compactStatusText') 
                    || this.shadowRoot.getElementById('desktopStatusText');
    if (!statusText) return;
    
    // Use custom statusText from config if provided, otherwise use translations
    const customStatusText = this.config.statusText;
    let statusMessage;
    if (customStatusText) {
      statusMessage = customStatusText;
    } else {
      const isListening = this.voiceInterface.isActive && !this.voiceInterface.isMicMuted;
      statusMessage = isListening ? this.t('listening') : this.t('connected');
    }
    statusText.textContent = statusMessage;
    
    // Also update the old one if it exists
    const oldStatusText = this.shadowRoot.getElementById('desktopStatusText');
    if (oldStatusText && oldStatusText !== statusText) {
      oldStatusText.textContent = statusMessage;
    }
  }
  
  /**
   * Update live waveform visualizer
   */
  updateLiveWaveform(isActive) {
    if (this.voiceInterface.isMobile) return;

    const liveWaveform = this.shadowRoot.getElementById('desktopLiveWaveform');
    if (!liveWaveform) return;

    const bars = liveWaveform.querySelectorAll('.desktop-live-bar');
    bars.forEach((bar, i) => {
      if (isActive) {
        const height = Math.random() * 40 + 8;
        bar.style.height = `${height}px`;
      } else {
        bar.style.height = '8px';
      }
    });
  }
  
  /**
   * Show error message
   */
  showError(message) {
    const transcriptEl = this.shadowRoot.getElementById('transcriptText');
    if (transcriptEl) {
      transcriptEl.textContent = `${this.t('error')}: ${message}`;
      transcriptEl.classList.remove('empty');
    }
  }
  
  /**
   * Show domain validation error
   * Shows error in idle state UI (inside widget) instead of transcript
   */
  showDomainError() {
    console.log('🚫 Showing domain error in widget UI...');
    const idleState = this.shadowRoot.getElementById('voiceIdleState');
    if (!idleState) {
      console.warn('⚠️ voiceIdleState element not found, cannot show domain error');
      return;
    }

    const title = this.t('domainNotValidated');
    const message = this.t('domainErrorMessage');

    // Mark for detection
    idleState.dataset.domainError = 'true';

    // --- Update hero section ---
    const headline = idleState.querySelector('.ttp-headline');
    const subline = idleState.querySelector('.ttp-subline');
    const onlineDot = idleState.querySelector('.ttp-online-dot');
    const agentRole = idleState.querySelector('.ttp-agent-role');

    if (headline) {
      headline.textContent = title;
      headline.style.color = '#fca5a5';
    }
    if (subline) {
      subline.textContent = message;
      subline.style.color = 'rgba(252,165,165,0.6)';
    }
    if (onlineDot) {
      onlineDot.style.background = '#ef4444';
    }
    if (agentRole) {
      agentRole.textContent = 'Unavailable';
      agentRole.style.color = 'rgba(252,165,165,0.5)';
    }

    // --- Hide existing action buttons, inject error card ---
    const body = idleState.querySelector('.ttp-body');
    if (body) {
      const startCallBtn = body.querySelector('#startCallBtn');
      const startTextBtn = body.querySelector('#startTextBtn');
      if (startCallBtn) startCallBtn.style.display = 'none';
      if (startTextBtn) startTextBtn.style.display = 'none';

      // Remove any previous error card
      const prev = body.querySelector('.ttp-domain-error-card');
      if (prev) prev.remove();
      const prevDismiss = body.querySelector('#domainErrorDismissBtn');
      if (prevDismiss) prevDismiss.remove();

      const card = document.createElement('div');
      card.className = 'ttp-domain-error-card';
      card.innerHTML = `
        <div class="ttp-domain-error-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fca5a5" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>
        <div class="ttp-domain-error-text">
          This website's domain is not authorised to use this agent. The site owner needs to whitelist <strong>${window.location.hostname}</strong>.
        </div>
      `;
      body.appendChild(card);

      const dismissBtn = document.createElement('button');
      dismissBtn.type = 'button';
      dismissBtn.className = 'ttp-btn-secondary';
      dismissBtn.id = 'domainErrorDismissBtn';
      dismissBtn.innerHTML = `
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
        Dismiss
      `;
      dismissBtn.onclick = () => {
        this.voiceInterface.isShowingDomainError = false;
        this.voiceInterface.resetConnectingState();
        if (this.voiceInterface.config.onShowLanding && typeof this.voiceInterface.config.onShowLanding === 'function') {
          this.voiceInterface.config.onShowLanding();
        }
      };
      body.appendChild(dismissBtn);
    }

    // Hide active state, ensure idle is visible
    const activeState = this.shadowRoot.getElementById('voiceActiveState');
    if (activeState) activeState.style.display = 'none';
    idleState.style.display = 'flex';

    console.log('✅ Domain error displayed in new desktop UI');
  }

  /**
   * Show warning message when back button is clicked during active call
   * Shows message in the transcript area (always visible, doesn't expand conversation panel)
   */
  showBackButtonWarning() {
    // Show warning in the transcript area (always visible, no need to expand conversation panel)
    const transcriptEl = this.shadowRoot.getElementById('transcriptText');
    const liveTextCollapsed = this.shadowRoot.getElementById('liveTextCollapsed');
    
    // Try transcript area first (for active call state)
    if (transcriptEl) {
      const originalText = transcriptEl.textContent;
      const originalHTML = transcriptEl.innerHTML;
      
      transcriptEl.innerHTML = `
        <div style="font-weight: 600; font-size: 16px; margin-bottom: 8px; color: #92400e; display: flex; align-items: center; gap: 8px;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink: 0;">
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          Please end the call first
        </div>
        <div style="font-size: 14px; color: #92400e; line-height: 1.5;">
          You need to end the current call before going back.
        </div>
      `;
      transcriptEl.classList.remove('empty');
      
      // Restore original text after 5 seconds
      setTimeout(() => {
        if (transcriptEl) {
          transcriptEl.innerHTML = originalHTML;
          transcriptEl.textContent = originalText;
          if (!originalText || originalText.trim() === '') {
            transcriptEl.classList.add('empty');
          }
        }
      }, 5000);
    } else if (liveTextCollapsed) {
      // Fallback: show in collapsed live transcript area
      const originalText = liveTextCollapsed.textContent;
      liveTextCollapsed.innerHTML = `
        <span style="color: #92400e; font-weight: 600;">⚠️ Please end the call first - You need to end the current call before going back.</span>
      `;
      
      // Restore original text after 5 seconds
      setTimeout(() => {
        if (liveTextCollapsed) {
          liveTextCollapsed.textContent = originalText;
        }
      }, 5000);
    }
  }

  /**
   * Start live waveform animation interval
   */
  startLiveWaveformInterval() {
    if (this.voiceInterface.isMobile) return;
    
    // Clear existing interval if any
    if (this._liveWaveformInterval) {
      clearInterval(this._liveWaveformInterval);
    }
    
    this._liveWaveformInterval = setInterval(() => {
      this.updateLiveWaveform(this.voiceInterface.isActive && !this.voiceInterface.isMicMuted);
    }, 100);
  }

  /**
   * Stop live waveform animation interval
   */
  stopLiveWaveformInterval() {
    if (this._liveWaveformInterval) {
      clearInterval(this._liveWaveformInterval);
      this._liveWaveformInterval = null;
    }
  }

  /**
   * Cleanup all desktop intervals and resources
   */
  cleanup() {
    this.stopLiveWaveformInterval();
    this.stopDesktopWaveformAnimation();
  }
}
